import React from 'react';
import ReactDOM from 'react-dom/client';
import { Switch } from 'react-router';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import * as uuid from 'uuid';

import 'store/modules/fastSearch';
import './config/i18n';

import store, { history } from 'store';

import { SENTRY_DSN, SENTRY_ENV, SENTRY_RELEASE, ANALYTICS_SESSION_UUID_KEY } from 'config';

import headerMeta from 'config/meta';
import headerLink from 'config/link';
import entryRoutes from 'routing/entry';
import { getRoutes } from 'routing';

import { theme } from 'styles';
import './styles/css/core.css';
import './styles/css/tailwindcssloader.css';
import './styles/fonts/HurmeGeometricSans2.css';
import './styles/fonts/NoeDisplay.css';
import { writeToSessionStorage } from 'services/windowSessionStorage';
import { initSentry } from 'integrations/sentry/initSentry';
import { TitleFavicon } from 'components/TitleFavicon';

// sessionUuid is used by analytics 3rd parties
const sessionUuid = uuid.v4();

// overwrite false because we specifically want the same session ID to last over tab reloads
writeToSessionStorage(ANALYTICS_SESSION_UUID_KEY, sessionUuid, { overwrite: false });

initSentry(SENTRY_DSN, SENTRY_ENV, SENTRY_RELEASE, sessionUuid);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      gcTime: 1000 * 180, // 3 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const bootstrap = () => {
  const root = ReactDOM.createRoot(document.getElementById('app'));
  root.render(
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <Helmet>
            {headerMeta}
            {headerLink}
          </Helmet>
          <TitleFavicon />
          <ConnectedRouter history={history}>
            <Switch>{getRoutes(entryRoutes)}</Switch>
          </ConnectedRouter>
        </QueryClientProvider>
      </Provider>
    </ThemeProvider>
  );
};

// why-did-you-render must patch React BEFORE the first render — patching mid-flight changes
// hook order between renders and corrupts component state. Direct process.env.NODE_ENV check
// (not the config re-export) so bundlers statically eliminate the branch — and the dynamic
// import with it — in production builds.
if (process.env.NODE_ENV !== 'production') {
  import('@welldone-software/why-did-you-render').then(({ default: whyDidYouRender }) => {
    whyDidYouRender(React);
    bootstrap();
  });
} else {
  bootstrap();
}
